import React from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { useStore } from 'src/hooks/useStore';
import { PLURAL, SINGULAR } from 'src/utils/constants';
import { formatNameList } from 'src/utils/utils';
import HomeWidgetTextStore from 'src/stores/HomeWidgetTextStore';
import { useDailyHabitInterstitialMessageStyles } from './DailyHabitsInterstitialMessage.styles';

interface DailyHabitsInterstitialMessageProps {
  completedPlanNames: string[]
  failedPlanNames: string[]
  className?: string
}

type MessageTemplates = {
  title: string
  bodyLines: string[]
};

const getMessageTemplates = (
  completedPlansCount: number,
  failedPlansCount: number,
  homeWidgetTextStore: HomeWidgetTextStore,
): MessageTemplates => {
  const t = (key: string): string => homeWidgetTextStore.getHomeWidgetText(key);
  const plural = (count: number): string => (count > 1 ? PLURAL : SINGULAR);

  const hasCompleted = completedPlansCount > 0;
  const hasFailed = failedPlansCount > 0;

  if (hasCompleted && hasFailed) {
    return {
      title: t('DailyHabitsWidget:Communications:CompletedInterstitialTitle'),
      bodyLines: [
        t(`DailyHabitsWidget:Communications:PlanMixedCompleted${plural(completedPlansCount)}`),
        t(`DailyHabitsWidget:Communications:PlanMixedFailed${plural(failedPlansCount)}`),
        t(`DailyHabitsWidget:Communications:PlanMixedFailedBody3${plural(completedPlansCount)}`),
      ],
    };
  }

  if (hasCompleted) {
    return {
      title: t('DailyHabitsWidget:Communications:CompletedInterstitialTitle'),
      bodyLines: [
        t(`DailyHabitsWidget:Communications:PlanCompletedBody1${plural(completedPlansCount)}`),
        t(`DailyHabitsWidget:Communications:PlanCompletedBody2${plural(completedPlansCount)}`),
      ],
    };
  }

  if (hasFailed) {
    return {
      title: t('DailyHabitsWidget:Communications:FailedInterstitialTitle'),
      bodyLines: [
        t(`DailyHabitsWidget:Communications:PlanFailedBody1${plural(failedPlansCount)}`),
        t('DailyHabitsWidget:Communications:PlanFailedBody2'),
      ],
    };
  }

  return {
    title: t('DailyHabitsWidget:Communications:FailedInterstitialTitle'),
    bodyLines: [],
  };
};

const replaceText = (text: string, replacements: Record<string, string>): string =>
  Object.entries(replacements).reduce((acc, [key, value]) => acc.replace(key, value), text);

const DailyHabitsInterstitialMessage: React.FC<DailyHabitsInterstitialMessageProps> = ({
  completedPlanNames,
  failedPlanNames,
  className,
}) => {
  const classes = useDailyHabitInterstitialMessageStyles();
  const { homeWidgetTextStore } = useStore();

  const { title, bodyLines } = getMessageTemplates(
    completedPlanNames.length,
    failedPlanNames.length,
    homeWidgetTextStore,
  );

  const replacements = {
    '{__COMPLETEDPLANNAMES__}': formatNameList(completedPlanNames),
    '{__FAILEDPLANNAMES__}': formatNameList(failedPlanNames),
  };

  return (
    <div
      className={mergeClasses('textWrapper', classes.textWrapper, className)}
      data-testid="daily-habits-interstitial-message-container"
    >
      <p className={mergeClasses('title', classes.title)}>{title}</p>

      {bodyLines.map(line => (
        <p key={line} className={mergeClasses('description', classes.description)}>
          {replaceText(line, replacements)}
        </p>
      ))}
    </div>
  );
};

export default DailyHabitsInterstitialMessage;
